/* rev: sticky-bar quantity selector + off-top show trigger */
/* ==========================================================================
   PURA — Global JavaScript
   Shared, vanilla-JS interactions for the PURA theme. No frameworks, no
   libraries. Loaded globally via layout/theme.liquid.

   All cart interactions use the Shopify Ajax Cart API (POST /cart/add.js).

   Implemented:
     - puraInitNav()            Mobile slide-down nav drawer
     - puraInitAccordion()      FAQ — one open at a time (<details>)
     - puraInitProductGallery() Thumbnail → main image swap
     - puraInitColor()          Colour swatch → active state + variant id
     - puraInitBundle()         Bundle select → quantity + price display
     - puraInitAddToCart()      Ajax add to cart + cart count badge
     - puraInitStickyBar()      Show once the main ATC scrolls off the top;
                                mirrors add to cart + buy-box quantity selector
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 900;

  /* ----- Mobile nav drawer ---------------------------------------------- */
  function puraInitNav() {
    var toggle = document.querySelector('[data-pura-nav-toggle]');
    var drawer = document.getElementById('pura-nav-drawer');
    if (!toggle || !drawer) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', String(open));
      drawer.setAttribute('aria-hidden', String(!open));
      drawer.classList.toggle('is-open', open);
      document.body.classList.toggle('pura-nav-open', open);
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    drawer.addEventListener('click', function (event) {
      if (event.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
    window.addEventListener('resize', function () {
      if (window.innerWidth > MOBILE_BREAKPOINT) setOpen(false);
    });
  }

  /* ----- FAQ accordion (one open at a time) ----------------------------- */
  function puraInitAccordion() {
    var groups = document.querySelectorAll('[data-pura-accordion]');
    groups.forEach(function (group) {
      var items = group.querySelectorAll('details');
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ----- Product gallery (scroll-snap carousel) ------------------------- */
  function puraInitProductGallery() {
    document.querySelectorAll('[data-pura-gallery]').forEach(function (gallery) {
      var track = gallery.querySelector('[data-pura-gallery-track]');
      if (!track) return;
      var slides = track.querySelectorAll('[data-pura-gallery-slide]');
      var thumbs = gallery.querySelectorAll('[data-pura-gallery-thumb]');
      var dots = gallery.querySelectorAll('[data-pura-gallery-dot]');
      if (slides.length < 1) return;

      function scrollToIndex(index) {
        var slide = slides[index];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }

      var thumbsTrack = gallery.querySelector('[data-pura-gallery-thumbs]');

      function scrollThumbIntoView(index) {
        if (!thumbsTrack) return;
        var thumb = thumbs[index];
        if (!thumb) return;
        var left = thumb.offsetLeft - (thumbsTrack.clientWidth - thumb.clientWidth) / 2;
        thumbsTrack.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }

      function setActive(index) {
        thumbs.forEach(function (t) {
          t.classList.toggle('is-active', Number(t.getAttribute('data-index')) === index);
        });
        dots.forEach(function (d) {
          d.classList.toggle('is-active', Number(d.getAttribute('data-index')) === index);
        });
        scrollThumbIntoView(index);
      }

      function nearestIndex() {
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < slides.length; i++) {
          var s = slides[i];
          var dist = Math.abs(s.offsetLeft + s.clientWidth / 2 - center);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        }
        return best;
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = Number(thumb.getAttribute('data-index')) || 0;
          scrollToIndex(idx);
          setActive(idx);
        });
      });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var idx = Number(dot.getAttribute('data-index')) || 0;
          scrollToIndex(idx);
          setActive(idx);
        });
      });

      var scrollTimer = null;
      track.addEventListener('scroll', function () {
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () { setActive(nearestIndex()); }, 60);
      }, { passive: true });

      /* Desktop: let the thumb strip be dragged with the cursor and let a
         vertical mouse wheel scroll it horizontally. Snap is temporarily
         disabled while dragging so momentum feels natural. */
      if (thumbsTrack) {
        var isDown = false;
        var startX = 0;
        var startScroll = 0;
        var moved = false;

        thumbsTrack.addEventListener('mousedown', function (e) {
          isDown = true;
          moved = false;
          startX = e.pageX;
          startScroll = thumbsTrack.scrollLeft;
          thumbsTrack.classList.add('is-dragging');
        });

        window.addEventListener('mousemove', function (e) {
          if (!isDown) return;
          var dx = e.pageX - startX;
          if (Math.abs(dx) > 3) moved = true;
          thumbsTrack.scrollLeft = startScroll - dx;
        });

        window.addEventListener('mouseup', function () {
          if (!isDown) return;
          isDown = false;
          thumbsTrack.classList.remove('is-dragging');
        });

        /* Suppress the thumbnail click if the user was dragging. */
        thumbsTrack.addEventListener('click', function (e) {
          if (moved) {
            e.stopPropagation();
            e.preventDefault();
            moved = false;
          }
        }, true);

        /* Vertical wheel → horizontal scroll on the thumb strip. */
        thumbsTrack.addEventListener('wheel', function (e) {
          var delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          if (delta === 0) return;
          thumbsTrack.scrollLeft += delta;
          e.preventDefault();
        }, { passive: false });
      }
    });

    /* Legacy single-image gallery (combbrush PDP): main image + thumbnails
       that swap the src. Browsers ignore src updates when srcset is present,
       so we clear srcset/sizes on swap. */
    document.querySelectorAll('[data-pura-gallery-main]').forEach(function (mainWrap) {
      var img = mainWrap.querySelector('img');
      if (!img) return;
      var scope = mainWrap.closest('section') || document;
      var legacyThumbs = scope.querySelectorAll('[data-pura-gallery-thumb][data-full]');
      if (!legacyThumbs.length) return;

      legacyThumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (full) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = full;
          }
          legacyThumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ----- Shared buy-state ----------------------------------------------- */
  var buyState = { variantId: null, quantity: 1, price: '' };

  function puraUpdatePriceDisplays() {
    document.querySelectorAll('[data-pura-price-display]').forEach(function (el) {
      if (buyState.price) el.textContent = buyState.price;
    });
    document.querySelectorAll('[data-pura-sticky-price]').forEach(function (el) {
      if (buyState.price) el.textContent = buyState.price;
    });
  }

  /* ----- Colour swatches ------------------------------------------------ */
  function puraInitColor() {
    var swatches = document.querySelectorAll('[data-pura-color]');
    var variantInput = document.querySelector('[data-pura-variant-id]');
    var nameEl = document.querySelector('[data-pura-color-name]');
    if (!swatches.length) return;

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        swatches.forEach(function (s) {
          s.classList.remove('is-active');
          s.setAttribute('aria-checked', 'false');
        });
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-checked', 'true');

        var id = swatch.getAttribute('data-variant-id');
        if (id) {
          buyState.variantId = id;
          if (variantInput) variantInput.value = id;
        }
        if (nameEl) nameEl.textContent = swatch.getAttribute('data-color-name');

        var price = swatch.getAttribute('data-price');
        if (price) {
          buyState.price = price;
          puraUpdatePriceDisplays();
        }
      });
    });
  }

  /* ----- Quantity stepper ----------------------------------------------- */
  function puraInitQtyStepper() {
    var input = document.querySelector('[data-pura-qty-input]');
    var minus = document.querySelector('[data-pura-qty-minus]');
    var plus = document.querySelector('[data-pura-qty-plus]');
    if (!input) return;

    function sync() {
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      input.value = v;
      buyState.quantity = v;
    }

    if (minus) minus.addEventListener('click', function () {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      sync();
    });
    if (plus) plus.addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
      sync();
    });
    input.addEventListener('change', sync);
    sync();
  }

  /* ----- Bundle selector ------------------------------------------------ */
  /* Dynamic "Ships by" date — today + N days (Asia/Manila), e.g. "Aug 17, 2026".
     Fills every .ships-by-date; N comes from data-days (default 3). */
  function puraInitShipsBy() {
    var els = document.querySelectorAll('.ships-by-date');
    if (!els.length) return;
    els.forEach(function (el) {
      var days = parseInt(el.getAttribute('data-days'), 10);
      if (isNaN(days)) days = 3;
      var d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
      d.setDate(d.getDate() + days);
      el.textContent = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    });
  }

  function puraInitBundle() {
    var bundles = document.querySelectorAll('[data-pura-bundle]');
    var qtyInput = document.querySelector('[data-pura-qty]');
    if (!bundles.length) return;

    function select(bundle) {
      bundles.forEach(function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-checked', 'false');
      });
      bundle.classList.add('is-active');
      bundle.setAttribute('aria-checked', 'true');

      buyState.quantity = parseInt(bundle.getAttribute('data-qty'), 10) || 1;
      buyState.price = bundle.getAttribute('data-price') || buyState.price;
      if (qtyInput) qtyInput.value = buyState.quantity;
      puraUpdatePriceDisplays();
    }

    bundles.forEach(function (bundle) {
      bundle.addEventListener('click', function () { select(bundle); });
      bundle.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          select(bundle);
        }
      });
    });

    // Initialise quantity from the pre-selected tier so a default tier with
    // quantity > 1 is reflected before any interaction.
    var active = document.querySelector('[data-pura-bundle].is-active') || bundles[0];
    if (active) {
      buyState.quantity = parseInt(active.getAttribute('data-qty'), 10) || 1;
      if (qtyInput) qtyInput.value = buyState.quantity;
    }
  }

  /* ----- Cart drawer ---------------------------------------------------- */
  var CART_SECTION = 'pura-cart-drawer';

  function puraCartEl() {
    return document.querySelector('[data-pura-cart]');
  }

  function puraCartOpen() {
    var cart = puraCartEl();
    if (!cart) return;
    cart.classList.add('is-open');
    cart.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pura-cart-open');
  }

  function puraCartClose() {
    var cart = puraCartEl();
    if (!cart) return;
    cart.classList.remove('is-open');
    cart.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pura-cart-open');
  }

  function puraRefreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var link = document.querySelector('.pura-header__cart');
        if (!link) return;
        var badge = link.querySelector('.pura-header__cart-count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'pura-header__cart-count';
          link.appendChild(badge);
        }
        badge.textContent = cart.item_count;
      })
      .catch(function () {});
  }

  function puraCartRender(html) {
    if (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-pura-cart-contents]');
      var live = document.querySelector('[data-pura-cart-contents]');
      if (fresh && live) live.innerHTML = fresh.innerHTML;
    }
    puraRefreshCartCount();
  }

  function puraCartFetch() {
    fetch(window.location.pathname + '?sections=' + CART_SECTION, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { puraCartRender(data[CART_SECTION]); })
      .catch(function () {});
  }

  function puraCartAddVariant(id, quantity, button, sellingPlan) {
    if (!id) return;
    if (button) button.disabled = true;
    var body = { id: id, quantity: quantity || 1, sections: CART_SECTION, sections_url: window.location.pathname };
    if (sellingPlan) body.selling_plan = sellingPlan;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        puraCartRender(data.sections ? data.sections[CART_SECTION] : null);
        puraCartOpen();
        if (button) button.disabled = false;
      })
      .catch(function () { if (button) button.disabled = false; });
  }

  function puraCartChange(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: CART_SECTION, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { puraCartRender(cart.sections ? cart.sections[CART_SECTION] : null); })
      .catch(function () {});
  }

  /* Read the current selection straight from the product form (variant id,
     selling plan, quantity) — works for both the native form and the
     swatch/stepper hero. */
  function puraReadSelection() {
    var form = document.getElementById('pura-pdp-form');
    if (!form) return null;
    var idEl = form.querySelector('[data-pura-variant-id]');
    var planEl = form.querySelector('[name="selling_plan"]'); // set by the Loop Subscriptions widget
    // Read from the theme stepper if present, otherwise from any name="quantity"
    // input a bundle / volume-discount app (e.g. Rapi Bundle) updates on selection.
    var qtyEl = form.querySelector('[data-pura-qty]') || form.querySelector('[name="quantity"]');
    return {
      id: idEl ? idEl.value : null,
      plan: planEl ? planEl.value : '',
      quantity: qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1
    };
  }

  /* PDP form / sticky bar add */
  function puraAddToCart(button) {
    var sel = puraReadSelection();
    if (!sel) return;
    puraCartAddVariant(sel.id, sel.quantity, button, sel.plan);
  }

  /* Third-party widgets (Loop Subscriptions) hunt for button[name="add"]
     and overwrite its innerHTML on load and on every variant/plan change.
     We stamp the merchant-set label into data-pura-atc-label and pin it
     back whenever anything mutates the button's text. */
  function puraLockAtcLabel() {
    document.querySelectorAll('[data-pura-atc-label]').forEach(function (btn) {
      var desired = btn.getAttribute('data-pura-atc-label');
      if (!desired) return;

      function apply() {
        var textEl = btn.querySelector('[data-pura-atc-text]');
        if (textEl) {
          if (textEl.textContent !== desired) textEl.textContent = desired;
        } else if (btn.textContent !== desired) {
          btn.textContent = desired;
        }
      }
      apply();

      var observer = new MutationObserver(function () {
        if (btn.dataset.puraApplying === '1') return;
        btn.dataset.puraApplying = '1';
        apply();
        delete btn.dataset.puraApplying;
      });
      observer.observe(btn, { childList: true, subtree: true, characterData: true });
    });
  }

  function puraInitAddToCart() {
    var form = document.getElementById('pura-pdp-form');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        puraAddToCart(form.querySelector('[data-pura-atc]'));
      });
    }

    puraLockAtcLabel();

    var cart = puraCartEl();
    if (cart) {
      cart.addEventListener('click', function (event) {
        if (event.target.closest('[data-pura-cart-close]')) { puraCartClose(); return; }
        var change = event.target.closest('[data-pura-cart-change]');
        if (change) {
          puraCartChange(parseInt(change.getAttribute('data-line'), 10), parseInt(change.getAttribute('data-qty'), 10));
          return;
        }
        var add = event.target.closest('[data-pura-cart-add]');
        if (add) puraCartAddVariant(add.getAttribute('data-variant-id'), parseInt(add.getAttribute('data-qty'), 10) || 1, add);
      });

      // Refill upsell: selecting a quantity option updates the Add button's qty.
      cart.addEventListener('change', function (event) {
        var opt = event.target.closest('[data-pura-refill-opt]');
        if (!opt) return;
        var box = opt.closest('[data-pura-refill]');
        var addBtn = box ? box.querySelector('[data-pura-cart-add]') : null;
        if (addBtn) addBtn.setAttribute('data-qty', opt.getAttribute('data-qty') || '1');
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') puraCartClose();
    });

    var trigger = document.querySelector('[data-pura-cart-toggle]');
    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        puraCartOpen();
        puraCartFetch();
      });
    }
  }

  /* ----- Sticky add-to-cart bar ----------------------------------------- */
  function puraInitStickyBar() {
    var bar = document.querySelector('[data-pura-sticky-bar]');
    if (!bar) return;

    // Show the sticky bar only once the main add-to-cart button has fully
    // scrolled off the top of the viewport (falls back to a scroll threshold).
    var mainAtc = document.querySelector('[data-pura-atc]');
    function onScroll() {
      var show = mainAtc ? mainAtc.getBoundingClientRect().bottom <= 0 : window.scrollY > 500;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();

    // Quantity selector mirrored from the buy-box tiers (quantity + per-each
    // price). Selecting an option drives the same quantity as the buy box.
    var stickySelect = bar.querySelector('[data-pura-sticky-select]');
    if (stickySelect) {
      var bundles = document.querySelectorAll('[data-pura-bundle]');
      if (!bundles.length) {
        stickySelect.style.display = 'none';
      } else {
        bundles.forEach(function (b, i) {
          var opt = document.createElement('option');
          opt.value = b.getAttribute('data-qty') || '1';
          var titleEl = b.querySelector('.pura-tier__title');
          var priceEl = b.querySelector('.pura-tier__now');
          var label = titleEl ? titleEl.textContent.trim() : ('Qty ' + opt.value);
          var price = priceEl ? priceEl.textContent.trim() : '';
          opt.textContent = price ? (label + ' — ' + price) : label;
          if (b.classList.contains('is-active')) opt.selected = true;
          stickySelect.appendChild(opt);
          // Keep the dropdown in sync when a buy-box tier is chosen.
          b.addEventListener('click', function () { stickySelect.selectedIndex = i; });
        });
        stickySelect.addEventListener('change', function () {
          var b = bundles[stickySelect.selectedIndex];
          if (b) b.click();
        });
      }
    }

    var stickyBtn = bar.querySelector('[data-pura-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        puraAddToCart(stickyBtn);
      });
    }
  }

  /* ----- Native variant + selling-plan picker (PURA One PDP) ------------ */
  function puraInitProductForm() {
    var form = document.getElementById('pura-pdp-form');
    if (!form) return;
    var dataEl = form.querySelector('[data-pura-variants]');
    if (!dataEl) return; // only the native form carries variant data

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var idInput = form.querySelector('[data-pura-variant-id]');
    var atc = form.querySelector('[data-pura-atc]');

    function selectedValues() {
      return Array.prototype.map.call(
        form.querySelectorAll('[data-pura-option]:checked'),
        function (i) { return i.value; }
      );
    }

    function matchVariant(values) {
      if (!values.length) return variants[0];
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.options || v.options.length !== values.length) continue;
        var ok = true;
        for (var j = 0; j < values.length; j++) {
          if (v.options[j] !== values[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    function update() {
      var variant = matchVariant(selectedValues());
      if (!variant) {
        if (atc) atc.disabled = true;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (atc) atc.disabled = !variant.available;

      form.querySelectorAll('[data-pura-option-label]').forEach(function (label) {
        var pos = label.getAttribute('data-pura-option-label');
        var checked = form.querySelector('[data-pura-option][data-position="' + pos + '"]:checked');
        if (checked) label.textContent = checked.value;
      });
    }

    form.addEventListener('change', function (event) {
      if (event.target.matches('[data-pura-option]')) update();
    });
    update();
  }

  /* ----- Video slider: inline play + dot pagination --------------------- */
  function puraInitVideoSliders() {
    document.querySelectorAll('[data-pura-video]').forEach(function (slider) {
      var track = slider.querySelector('[data-pura-video-track]');

      // Inline play: clone the deferred <template> video into the frame.
      slider.querySelectorAll('[data-pura-video-play]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var thumb = btn.closest('.pura-video__thumb');
          var tpl = thumb && thumb.querySelector('[data-pura-video-template]');
          if (!tpl) return;
          thumb.appendChild(tpl.content.cloneNode(true));
          btn.remove();
          var video = thumb.querySelector('video');
          if (video && video.play) video.play();
        });
      });

      // Dot pagination.
      var dots = slider.querySelectorAll('[data-pura-video-dot]');
      if (!track || dots.length < 2) return;
      var cards = track.children;

      function setActive(index) {
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      function nearestIndex() {
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < cards.length; i++) {
          var c = cards[i];
          var dist = Math.abs(c.offsetLeft + c.clientWidth / 2 - center);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        }
        return best;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var c = cards[i];
          if (!c) return;
          track.scrollTo({
            left: c.offsetLeft - (track.clientWidth - c.clientWidth) / 2,
            behavior: 'smooth'
          });
        });
      });

      track.addEventListener('scroll', function () {
        setActive(nearestIndex());
      }, { passive: true });
    });
  }

  /* ----- Boot ----------------------------------------------------------- */
  function puraInit() {
    puraInitNav();
    puraInitAccordion();
    puraInitProductGallery();
    puraInitColor();
    puraInitBundle();
    puraInitShipsBy();
    puraInitProductForm();
    puraInitQtyStepper();
    puraInitAddToCart();
    puraInitStickyBar();
    puraInitVideoSliders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', puraInit);
  } else {
    puraInit();
  }
})();
